import { Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-date-picker";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import useInventory from "../../../../Hooks/useInventory";
import useProducts from "../../../../Hooks/useProduct";
import { dateFormatter } from "../../../../utils/commonUtils";
import Loading from "../../../../components/LoadingPage/Loading";
import moment from "moment";
import _ from "lodash";
import { selectCurrentUserId } from "../../../../redux/auth/authSlice";
import { useAppSelector } from "../../../../redux/hooks";
import {
  useModulePermissions,
  PermissionIdentifier,
  PermissionModule,
} from "../../../../utils/permissionUtils";
import { useMemo, useState } from "react";

export const LookUpModel = ({
  show,
  handleClose,
  renderItemLookUpPage,
  setRenderItemLookUpPage,
  setFormBarcodeValues,
  srxBarcode,
}: any) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const isPermitted = useModulePermissions(PermissionModule.INVENTORY);
  const isCheckInternetPermitted = isPermitted(PermissionIdentifier.CHECK_NDC_ON_INTERNET);
  const isAddToProductMasterPermitted = isPermitted(PermissionIdentifier.ADD_TO_NDC_PRODUCT_MASTER);
  const { getBarcodeDecode, barcodeDetailsLoading } = useInventory();
  const { upsertProductAPI, upsertProductLoading } = useProducts();
  const [decodedBarcode, setDecodedBarcode] = useState<string>("");

  const initialBarcodeFormValues = useMemo(() => {
    if (!renderItemLookUpPage) return {};
    let updatedValues = { ...renderItemLookUpPage };

    if (!isCheckInternetPermitted) {
      updatedValues.checkInternet = false;
    }

    if (!isAddToProductMasterPermitted) {
      updatedValues.addNdcProductMaster = false;
    }

    return updatedValues;
  }, [
    renderItemLookUpPage,
    isCheckInternetPermitted,
    isAddToProductMasterPermitted,
  ]);

  const {
    handleChange: handleChangeBarcode,
    values: barcodeValues,
    handleSubmit: barcodeSubmit,
    touched: barcodeTouched,
    errors: barcodeErrors,
    resetForm: barcodeReset,
  } = useFormik({
    initialValues: initialBarcodeFormValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      addNdcProductMaster: Yup.boolean(),
      checkInternet: Yup.boolean(),
      isMedicine: Yup.boolean(),
      useMfrBarcode: Yup.boolean(),
      barcode: Yup.string()
        .required("Barcode is Required")
        .max(75, "Barcode should not be more than 75 characters"),
    }),
    onSubmit: async (
      barcodeFormValues: any,
      { setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response: any = await getBarcodeDecode(barcodeFormValues);
        setValues({
          cvx: response.cvX_Model,
          expiryDate:
            response.expirationDate &&
              moment(response.expirationDate, "MM/DD/YY", true).isValid()
              ? moment(dateFormatter(response.expirationDate))
                .utc()
                .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
              : "",
          lotNo: response.lotNumber,
          serialNumber: response.serialNumber || "NA",
          ndcCode: response.ndC_SKU,
          manufacturer: response.manufacturer,
          drugName: response.group,
          rxName: response.name,
          barcode: "",
          mvxCode: response.mvxCode,
          mvxManufacturer: response.mvxManufacturer,
          mvxStatus: response.mvxStatus,
        });
        if (barcodeFormValues.useMfrBarcode) {
          setFieldValue("barcode", response.barcode);
          setDecodedBarcode(response.barcode || "");
        }
      } catch (err: any) {
        console.error(err);
      }
      setSubmitting(false);
    },
  });

  const {
    handleChange,
    values,
    handleSubmit,
    setValues,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      rxName: "",
      drugName: "",
      manufacturer: "",
      ndcCode: "",
      lotNo: "",
      cvx: "",
      expiryDate: "",
      serialNumber: "",
      barcode: "",
      mvxCode: "",
      mvxManufacturer: "",
      mvxStatus: "",
    },
    validationSchema: Yup.object({
      rxName: Yup.string().required("Item Name is Required"),
      drugName: Yup.string().required("Item Group is Required"),
      manufacturer: Yup.string().required("Manufacturer is Required"),
      ndcCode: Yup.string().required("NDC is Required"),
      lotNo: Yup.string().required("LOT is Required"),
      cvx: Yup.string().required("CVX/Model is Required"),
      //  expiryDate: Yup.string().required("Expiration Date is Required"),
    }),
    onSubmit: async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      try {
        setFormBarcodeValues(values);
        setRenderItemLookUpPage({
          useMfrBarcode: barcodeValues?.useMfrBarcode,
          checkInternet: barcodeValues?.checkInternet,
          addNdcProductMaster: barcodeValues?.addNdcProductMaster,
          isMedicine: barcodeValues?.isMedicine,
          barcode: barcodeValues?.barcode,
        });
        const ndcValue = values.ndcCode?.trim();
        const isNdcValid = ndcValue && ndcValue.toUpperCase() !== "NA";
        if (barcodeValues?.addNdcProductMaster && isNdcValid) {
          try {
            await upsertProductAPI({
              productCode: values.ndcCode,
              barcode: values.ndcCode.split("-").join(""),
              productName: values.rxName,
              category: values.drugName,
              manufacturer: values.manufacturer,
              model: values.cvx,
              isMedicine: barcodeValues?.isMedicine,
              isActive: true,
              createdBy: currentUserId,
              mvxCode: values?.mvxCode,
              mvxManufacturer: values?.mvxManufacturer,
              mvxStatus: values?.mvxStatus,
            });
          } finally {
            barcodeReset();
            resetForm();
            setDecodedBarcode("");
            handleClose();
          }
        } else {
          barcodeReset();
          resetForm();
          setDecodedBarcode("");
          handleClose();
        }
      } catch (err: any) {
        console.error(err);
      }
      setSubmitting(false);
    },
    enableReinitialize: true,
  });

  return (
    <Modal
      size="lg"
      id="lookupmodal"
      show={show}
      onHide={() => {
        resetForm();
        barcodeReset();
        setDecodedBarcode("");
        handleClose();
      }}
    >
      {barcodeDetailsLoading && <Loading />}

      <Modal.Header closeButton>Look Up</Modal.Header>
      <Modal.Body>
        <div className="add-item-box">
          <Form onSubmit={barcodeSubmit}>
            <Form.Group className="form-field full-width">
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Form.Label style={{ fontWeight: "bold", marginBottom: 0 }}>SRX Barcode:</Form.Label>
                <span style={{ fontSize: "14px", fontWeight: "bold", color: "#0470A1" }}>
                  {barcodeValues.useMfrBarcode && decodedBarcode
                    ? decodedBarcode
                    : srxBarcode || "—"}
                </span>
              </div>
            </Form.Group>
            <Form.Group className="form-field full-width">
              <div className="item-radio-content">
                <div className="item-status-box">
                  <Form.Check
                    inline
                    label="Use “2D Barcode” for Tracking"
                    type="checkbox"
                    id="useMfrBarcode"
                    name="useMfrBarcode"
                    onChange={handleChangeBarcode}
                    value={barcodeValues.useMfrBarcode}
                    checked={barcodeValues.useMfrBarcode}
                  />
                </div>
                <div className="item-recall-box">
                  <div className="">
                    <Form.Check
                      inline
                      label="Check Internet"
                      name="checkInternet"
                      type="checkbox"
                      id="checkInternet"
                      onChange={handleChangeBarcode}
                      value={barcodeValues.checkInternet}
                      checked={barcodeValues.checkInternet}
                      disabled={!isCheckInternetPermitted}
                    />
                  </div>
                </div>
                <div className="item-recall-box">
                  <div className="">
                    <Form.Check
                      inline
                      label="Add to Product Master"
                      type="checkbox"
                      name="addNdcProductMaster"
                      id="addNdcProductMaster"
                      onChange={handleChangeBarcode}
                      value={barcodeValues.addNdcProductMaster}
                      checked={barcodeValues.addNdcProductMaster}
                      disabled={!isAddToProductMasterPermitted}
                    />
                  </div>
                </div>
                <div className="item-status-box">
                  <div className="">
                    <Form.Check
                      inline
                      label="Med"
                      type="radio"
                      name="isMedicine"
                      id="isMedicine"
                      onChange={(e) =>
                        handleChangeBarcode({
                          target: {
                            name: "isMedicine",
                            value: e.target.checked ? true : false,
                          },
                        })
                      }
                      value={barcodeValues.isMedicine}
                      checked={!!barcodeValues.isMedicine}
                    />
                    <Form.Check
                      inline
                      label="Non-Med"
                      type="radio"
                      onChange={(e) =>
                        handleChangeBarcode({
                          target: {
                            name: "isMedicine",
                            value: e.target.checked ? false : true,
                          },
                        })
                      }
                      value={barcodeValues.isMedicine}
                      checked={!!!barcodeValues.isMedicine}
                    />
                  </div>
                </div>
              </div>
            </Form.Group>
            <Form.Group className="form-field full-width inline">
              <Form.Label>Barcode</Form.Label>
              <div className="barcodebox">
                <Form.Control
                  type="text"
                  placeholder="Enter Barcode"
                  id="barcode"
                  name="barcode"
                  onChange={handleChangeBarcode}
                  value={barcodeValues.barcode}
                  isInvalid={barcodeTouched.barcode && !!barcodeErrors.barcode}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                />

                <button
                  type="submit"
                  disabled={!barcodeValues.barcode?.trim()}
                  className={barcodeValues.barcode?.trim() ? "active" : ""}
                >
                  Decode
                </button>
              </div>
              {barcodeTouched.barcode && barcodeErrors.barcode ? (
                <Form.Control.Feedback type="invalid">
                  {barcodeErrors.barcode as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field full-width">
              <Form.Label>Item Name*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Item Name"
                id="rxName"
                name="rxName"
                onChange={handleChange}
                value={values.rxName}
                isInvalid={touched.rxName && !!errors.rxName}
              />
              {touched.rxName && errors.rxName ? (
                <Form.Control.Feedback type="invalid">
                  {errors.rxName as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field full-width">
              <Form.Label>Item Group*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Item Group"
                id="drugName"
                name="drugName"
                onChange={handleChange}
                value={values.drugName}
                isInvalid={touched.drugName && !!errors.drugName}
              />
              {touched.drugName && errors.drugName ? (
                <Form.Control.Feedback type="invalid">
                  {errors.drugName as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field full-width">
              <Form.Label>Manufacturer*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Manufacturer Name"
                id="manufacturer"
                name="manufacturer"
                onChange={handleChange}
                value={values.manufacturer}
                isInvalid={touched.manufacturer && !!errors.manufacturer}
              />
              {touched.manufacturer && errors.manufacturer ? (
                <Form.Control.Feedback type="invalid">
                  {errors.manufacturer as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>NDC*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter NDC"
                id="ndcCode"
                name="ndcCode"
                onChange={handleChange}
                value={values.ndcCode}
                onInput={(e: any) => {
                  e.target.value = (e.target.value ?? "").replace(/[^\d\-a-zA-Z]/g, "");
                }}
                isInvalid={touched.ndcCode && !!errors.ndcCode}
              />
              {touched.ndcCode && errors.ndcCode ? (
                <Form.Control.Feedback type="invalid">
                  {errors.ndcCode as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>CVX/Model*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter CVX/Model"
                id="cvx"
                name="cvx"
                onChange={handleChange}
                value={values.cvx}
                isInvalid={touched.cvx && !!errors.cvx}
              />
              {touched.cvx && errors.cvx ? (
                <Form.Control.Feedback type="invalid">
                  {errors.cvx as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>LOT*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter LOT Number"
                id="lotNo"
                name="lotNo"
                onChange={handleChange}
                value={values.lotNo}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                }}
                isInvalid={touched.lotNo && !!errors.lotNo}
              />
              {touched.lotNo && errors.lotNo ? (
                <Form.Control.Feedback type="invalid">
                  {errors.lotNo as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>Expiration Date</Form.Label>
              <div
                className={`calendar-box ${touched.expiryDate && errors.expiryDate ? "is-invalid" : ""
                  }`}
              >
                <DatePicker
                  locale="en-US"
                  format="MM/dd/yyyy"
                  openCalendarOnFocus={false}
                  className={`form-control ${touched.expiryDate && errors.expiryDate ? "is-invalid" : ""
                    }`}
                  value={values.expiryDate}
                  dayPlaceholder="dd"
                  monthPlaceholder="mm"
                  yearPlaceholder="yyyy"
                  showLeadingZeros={true}
                  onChange={(date) =>
                    handleChange({
                      target: { name: "expiryDate", value: date },
                    })
                  }
                  clearIcon={null}
                  calendarIcon={<i className="bi bi-calendar"></i>}
                />
              </div>
              {touched.expiryDate && errors.expiryDate ? (
                <div className="invalid-feedback d-block">
                  {errors.expiryDate as string}
                </div>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Serial Number"
                id="serialNumber"
                name="serialNumber"
                onChange={handleChange}
                value={values.serialNumber}
                isInvalid={touched.serialNumber && !!errors.serialNumber}
              />
              {touched.serialNumber && errors.serialNumber ? (
                <Form.Control.Feedback type="invalid">
                  {errors.serialNumber as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <div></div>
            <Form.Group className="form-field">
              <Form.Label>MVX Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter MVX Code"
                id="mvxCode"
                name="mvxCode"
                onChange={handleChange}
                value={values?.mvxCode}
                isInvalid={touched.mvxCode && !!errors.mvxCode}
              />
              {touched.mvxCode && errors.mvxCode ? (
                <Form.Control.Feedback type="invalid">
                  {errors.mvxCode as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-field">
              <Form.Label>MVX Manufacturer</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter MVX Manufacturer"
                id="mvxManufacturer"
                name="mvxManufacturer"
                onChange={handleChange}
                value={values?.mvxManufacturer}
                isInvalid={touched.mvxManufacturer && !!errors.mvxManufacturer}
              />
              {touched.mvxManufacturer && errors.mvxManufacturer ? (
                <Form.Control.Feedback type="invalid">
                  {errors.mvxManufacturer as string}
                </Form.Control.Feedback>
              ) : null}
            </Form.Group>
            <Form.Group className="form-actions full-width">
              <Button
                onClick={() => {
                  resetForm();
                  barcodeReset();
                  setDecodedBarcode("");
                  handleClose();
                }}
                variant="outline-primary"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (values.expiryDate) {
                    const expiryDate = moment(values.expiryDate);
                    const today = moment().startOf("day");

                    if (expiryDate.isBefore(today)) {
                      alert("Warning! Invalid Expiration Date - Expired Item!");
                    }
                  }
                  handleSubmit();
                }}
              >
                Verified, Use Current Values
              </Button>
            </Form.Group>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};
